/**
 * NetworkMonitor — platform-agnostic online/offline state with listener
 * fan-out and a hook into TanStack Query's `onlineManager`.
 *
 * Web and mobile bind their own platform sources to this monitor:
 *   - Web: navigator.onLine + window online/offline events + fetch(/health).
 *   - Mobile: @react-native-community/netinfo subscription.
 *
 * State is module-scoped (singleton) — there is exactly one connectivity
 * truth per app process.
 */

let isOnline = true;
let wasOffline = false;
const listeners = new Set<() => void>();
let onlineManagerCallback: ((online: boolean) => void) | null = null;

/**
 * Override the initial online status. Call this from the platform
 * binding once on app boot (e.g. from `navigator.onLine` on web or
 * the first NetInfo snapshot on mobile).
 */
export function setInitialOnlineStatus(online: boolean): void {
  isOnline = online;
}

/**
 * Register TanStack Query's `onlineManager.setOnline` so the query
 * client reacts to status changes immediately rather than polling.
 */
export function setOnlineManagerCallback(
  callback: ((online: boolean) => void) | null
): void {
  onlineManagerCallback = callback;
}

/**
 * Update the connectivity status. No-ops if status hasn't changed.
 * Sets the `wasOffline` flag when transitioning from offline → online so
 * consumers (e.g. an offline sync queue) can react one-shot to recovery.
 */
export function setActualOnlineStatus(online: boolean): void {
  if (isOnline === online) return;

  if (online && !isOnline) {
    wasOffline = true;
  }
  isOnline = online;

  listeners.forEach(listener => listener());

  if (onlineManagerCallback) {
    onlineManagerCallback(online);
  }
}

export function markOffline(): void {
  setActualOnlineStatus(false);
}

export function markOnline(): void {
  setActualOnlineStatus(true);
}

export function checkIsOnline(): boolean {
  return isOnline;
}

export function checkWasOffline(): boolean {
  return wasOffline;
}

/**
 * Clear the wasOffline one-shot flag. Hooks should call this after
 * consumers have had a chance to react to the recovery.
 */
export function clearWasOffline(): void {
  wasOffline = false;
}

/**
 * Subscribe to status changes. Returns an unsubscribe function.
 */
export function subscribeNetworkStatus(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
